import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { findDatesRange } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import { ModalControlModalProps, Tray } from '@frontend/design-system';
import { APPOINTMENT_TABS } from '../../constants';
import { useAppointmentAnalyticsV2ShallowStore } from '../../hooks/use-appointment-analytics-v2-store';
import { trackingIds } from '../../tracking-ids';
import { AppointmentContactTypeFilter } from '../filters/appointment-contact-type-filter';
import { AppointmentLocationFilter } from '../filters/appointment-location-filter';
import { AppointmentProviderFilter } from '../filters/appointment-provider-filter';
import { AppointmentTimePeriodFilter } from '../filters/appointment-time-period-filter';
import { AppointmentCancellationLeadTimeTrayFilter } from './appointment-cancellation-lead-time-tray-filter';
import { AppointmentConfirmationStatusTrayFilter } from './appointment-confirmation-status-tray-filter';
import { AppointmentLeadTimeTrayFilter } from './appointment-lead-time-tray-filter';
import { AppointmentNoShowRiskTrayFilter } from './appointment-no-show-risk-tray-filter';
import { AppointmentStatusTrayFilter } from './appointment-status-tray-filter';
import { FilterTrayActions } from './filter-tray-actions';

export const AppointmentFilterTray = (props: ModalControlModalProps) => {
  const { t } = useTranslation('analytics');
  const { tableFilters, activeTab, setTableFilters, defaultFilters } = useAppointmentAnalyticsV2ShallowStore(
    'tableFilters',
    'activeTab',
    'setTableFilters',
    'defaultFilters'
  );

  const activeFilters = tableFilters[activeTab] || {};
  const [draftFilters, setDraftFilters] = useState(activeFilters);

  useEffect(() => {
    if (props.show) {
      setDraftFilters(tableFilters[activeTab] || {});
    }
  }, [props.show, activeTab, tableFilters]);

  const handleFilterChange = (updatedFilters: Partial<typeof draftFilters>) => {
    setDraftFilters((prev) => ({ ...prev, ...updatedFilters }));
  };

  return (
    <Tray {...props} css={styles.tray} width='medium'>
      <SurfaceHeader.Root>
        <SurfaceHeader.Row>
          <SurfaceHeader.Title>{t('Filter Appointments')}</SurfaceHeader.Title>
          <SurfaceHeader.Actions>
            <SurfaceHeader.Icon iconName='x' onClick={props.onClose} />
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
      </SurfaceHeader.Root>

      <Tray.Body css={styles.body}>
        <AppointmentLocationFilter
          value={draftFilters.locationId}
          onChange={(locationId) => handleFilterChange({ locationId })}
          isTrayFilter
        />

        <AppointmentTimePeriodFilter
          activeTab={activeTab}
          upcomingDates={activeTab === APPOINTMENT_TABS.UPCOMING ? draftFilters : {}}
          pastDates={activeTab === APPOINTMENT_TABS.PAST ? draftFilters : {}}
          locationId={draftFilters.locationId}
          onChange={(dates) => handleFilterChange(dates)}
          isTrayFilter
        />

        <AppointmentContactTypeFilter
          value={draftFilters.contactType}
          onChange={(contactType) => handleFilterChange({ contactType })}
          isTrayFilter
        />

        <AppointmentProviderFilter
          locationId={draftFilters.locationId}
          value={draftFilters.provider}
          onChange={(provider) => handleFilterChange({ provider })}
          isTrayFilter
        />

        {activeTab === APPOINTMENT_TABS.PAST && (
          <>
            <AppointmentStatusTrayFilter
              value={draftFilters.status}
              onChange={(status) => handleFilterChange({ status })}
            />
            <AppointmentCancellationLeadTimeTrayFilter
              value={draftFilters.cancelLeadTime}
              onChange={(cancelLeadTime) => handleFilterChange({ cancelLeadTime })}
            />
          </>
        )}

        <AppointmentLeadTimeTrayFilter
          value={draftFilters.leadTime}
          onChange={(leadTime) => handleFilterChange({ leadTime })}
        />

        <AppointmentConfirmationStatusTrayFilter
          value={draftFilters.confirmationStatus}
          onChange={(confirmationStatus) => handleFilterChange({ confirmationStatus })}
        />

        <AppointmentNoShowRiskTrayFilter
          value={draftFilters.atRiskStatus}
          onChange={(atRiskStatus) => handleFilterChange({ atRiskStatus })}
        />
      </Tray.Body>

      <FilterTrayActions
        primaryAction={{
          label: t('Apply Filters'),
          onClick: () => {
            setTableFilters(draftFilters);
            props.onClose();
          },
          trackingId: trackingIds.filterTrayApply,
        }}
        secondaryAction={{
          label: t('Cancel'),
          onClick: props.onClose,
          trackingId: trackingIds.filterTrayCancel,
        }}
        clearAllAction={{
          label: t('Clear All Filters'),
          onClick: () => {
            const upcomingDateRange = findDatesRange({ offset: 7, range: 'day', type: 'future' });
            setDraftFilters({
              locationId: defaultFilters.locationId,
              startDate:
                activeTab === APPOINTMENT_TABS.UPCOMING
                  ? dayjs(upcomingDateRange.startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS')
                  : defaultFilters.startDate,
              endDate:
                activeTab === APPOINTMENT_TABS.UPCOMING
                  ? dayjs(upcomingDateRange.endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS')
                  : defaultFilters.endDate,
              contactType: undefined,
              provider: undefined,
              status: undefined,
              confirmationStatus: undefined,
              atRiskStatus: undefined,
              cancelLeadTime: undefined,
              leadTime: undefined,
            });
          },
          trackingId: trackingIds.filterTrayClearAll,
        }}
      />
    </Tray>
  );
};

const styles = {
  tray: css`
    padding: 0;
    @media (max-width: ${breakpoints.xsmall.max}px) {
      width: 100%;
    }
  `,
  body: css`
    padding: ${theme.spacing(1, 4)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `,
};
